import type { SlownessBucket } from "@gw/shared-types";
import { slownessLabel, slownessTooltip } from "@/lib/labels";
import { Tooltip } from "@/components/Tooltip";

interface Props {
  bucket: SlownessBucket;
  ratio?: number | null;
  peerMedian?: number;
  peerCount?: number;
  unit?: "bd" | "cd";
}

// Each bucket carries an icon in addition to its color so the signal isn't color-only —
// helps with color-blindness and at-a-glance scanning. Matches WCAG 2.1 Use of Color (1.4.1).
const ICON_BY_BUCKET: Record<SlownessBucket, string> = {
  Critical: "error",
  Slow: "warning",
  OnTrack: "check_circle",
  InsufficientData: "help_outline",
  InProgress: "schedule",
};

export function SlownessPill({ bucket, ratio, peerMedian, peerCount, unit = "bd" }: Props) {
  const cls = bucket.toLowerCase();
  const label = slownessLabel(bucket);
  const tip = slownessTooltip(bucket, ratio ?? null, peerMedian, peerCount, unit);

  const tipContent =
    tip.kind === "scored" ? (
      <>
        <p>
          This onboarding took <strong>{tip.ratio.toFixed(1)}×</strong> the typical duration for
          similar requests.
        </p>
        {tip.peer && <p>{tip.peer}</p>}
        <hr className="tt-rule" />
        <p className="tt-legend">≥1.25× = Slow · ≥2.0× = Critical</p>
      </>
    ) : (
      <p>{tip.text}</p>
    );

  return (
    <Tooltip content={tipContent}>
      <span
        className={"slow-pill " + cls}
        role="status"
        aria-label={`Slowness: ${label}${ratio != null ? `, ratio ${ratio.toFixed(1)} times peer median` : ""}`}
      >
        <span className="material-symbols-rounded" aria-hidden="true">
          {ICON_BY_BUCKET[bucket]}
        </span>
        {label}
        {ratio != null ? ` ${ratio.toFixed(1)}×` : ""}
      </span>
    </Tooltip>
  );
}
